import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  TrackByFunction,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { Store } from '@ngxs/store';
import {
  ACTIVITY_BODY_MARGIN_BOTTOM_PX,
  ActivityWithCategoryAndLocationsAndReportedDetails,
  ItemViewModel,
} from '@wilson/interfaces';
import {
  GanttDateToPositionPxPipe,
  SelectedItemOutput,
  ShiftIdWithPosition,
  ShiftMultiSelectHelperService,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  GanttIsActivityEndLocationMismatch,
  GanttIsActivityStartLocationMismatch,
  ShiftTimelinePipesModule,
} from '@wilson/shift-timeline/pipes';
import {
  ChangedKeyManagerService,
  DragAndDropModeService,
} from '@wilson/shift-timeline/services';
import {
  ShiftTimelineDataState,
  ShiftTimelineState,
} from '@wilson/shift-timeline/state';
import { TimelineAutoSizeActivityCardComponent } from '../TimelineAutoSizeActivityCard/timeline-auto-size-activity-card.component';

@Component({
  selector: 'wilson-timeline-auto-size-activities-row-within-shift',
  imports: [
    CommonModule,
    ShiftTimelinePipesModule,
    TimelineAutoSizeActivityCardComponent,
    GanttIsActivityStartLocationMismatch,
    GanttIsActivityEndLocationMismatch,
  ],
  templateUrl:
    './timeline-auto-size-activities-row-within-shift.component.html',
  styleUrls: [
    './timeline-auto-size-activities-row-within-shift.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [GanttDateToPositionPxPipe],
})
export class TimelineAutoSizeActivitiesRowWithinShiftComponent {
  private store = inject(Store);
  readonly viewRef = inject(ViewContainerRef);
  private ganttDateToPositionPx = inject(GanttDateToPositionPxPipe);
  readonly dragAndDropModeService = inject(DragAndDropModeService);
  private changedKeyManagerService = inject(ChangedKeyManagerService);
  readonly shiftMultiSelectHelperService = inject(
    ShiftMultiSelectHelperService,
  );

  @Input() rowActivities!: ActivityWithCategoryAndLocationsAndReportedDetails[];
  @Input() shift!: ItemViewModel['shift'];
  @Input() shiftWidthPx!: number | null;
  @Input() userId!: string;
  @Input() accurateShiftStartDateTime!: string;
  @Input() accurateShiftEndDateTime!: string;
  @Input() accurateShiftDurationMs!: number;
  @Input() filteredActivityId!: string | null;
  @Input() isCursorGrab!: boolean;
  @Input() isActivityDraggable!: boolean;

  @HostBinding('style.margin-bottom.px')
  get marginBottomPx(): number {
    return ACTIVITY_BODY_MARGIN_BOTTOM_PX;
  }

  @Output()
  selected = new EventEmitter<SelectedItemOutput>();

  trackByActivities: TrackByFunction<ActivityWithCategoryAndLocationsAndReportedDetails> =
    (
      _: number,
      activity: ActivityWithCategoryAndLocationsAndReportedDetails,
    ) => {
      return this.changedKeyManagerService.computeChangedKeyForActivity(
        activity,
      );
    };

  handleSelectedActivity(
    event: Event,
    activityId: string | undefined,
    startDate: string,
    endDate: string,
    selectedShifts: ShiftIdWithPosition[],
  ): void {
    if (selectedShifts.length <= 1) {
      event.preventDefault();
      event.stopPropagation();

      this.shiftMultiSelectHelperService.clearSelection();

      if (activityId) {
        const selectedData: SelectedItemOutput = {
          id: activityId,
          left: this.ganttDateToPositionPx.transform(new Date(startDate), {
            timelineWidthPixel: this.store.selectSnapshot(
              ShiftTimelineState.frameLength,
            ),
            interval: this.store.selectSnapshot(
              ShiftTimelineDataState.timeframe,
            ),
          }),
          right: this.ganttDateToPositionPx.transform(new Date(endDate), {
            fromRightSide: true,
            timelineWidthPixel: this.store.selectSnapshot(
              ShiftTimelineState.frameLength,
            ),
            interval: this.store.selectSnapshot(
              ShiftTimelineDataState.timeframe,
            ),
          }),
        };

        this.selected.emit(selectedData);
      }
    }
  }
}
